import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { sendRequest } from '@infrastructure/api/api';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { useEntityContext, usePermissions } from '@features/entity/hooks/usePermissions';
import { ACTION_CODES } from '@features/entity/types/actionCodes';
import DashboardSkeleton from '@features/dashboard/components/DashboardSkeleton';
import '@features/dashboard/pages/dashboard.css';
import OfficersCard from '../components/OfficersCard';
import MembershipSection from '../components/MembershipSection';
import HeadChurchLabel from '@features/district/components/HeadChurchLabel';
import type { MembershipSummary, OfficersCardData } from '../types';
import '../membership.css';

interface MyEntityPageData {
  officers?: OfficersCardData;
  membership?: MembershipSummary;
  membership_error?: boolean;
  district_name?: string | null;
  is_head_church?: boolean;
}

function pageLabel(scope: string): string {
  if (scope === 'DISTRICT') return 'My District';
  if (scope === 'NATION') return 'My Nation';
  return 'My Church';
}

function pageSlug(scope: string): string {
  if (scope === 'DISTRICT') return 'my-district';
  if (scope === 'NATION') return 'my-nation';
  return 'my-church';
}

const MyEntityPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{
    churchId?: string;
    districtId?: string;
    nationId?: string;
  }>();

  const churchId = Number(params.churchId) || 0;
  const districtId = Number(params.districtId) || 0;
  const nationId = Number(params.nationId) || 0;

  const scope = churchId > 0 ? 'CHURCH' : districtId > 0 ? 'DISTRICT' : 'NATION';
  const scopeId = churchId || districtId || nationId;
  const scopeSegment = getApiScopeFromValue(scope);
  const label = pageLabel(scope);
  const { context: entityContext } = useEntityContext();
  const scopeName = entityContext?.scopeName?.trim();
  const title = scopeName ? `${label} · ${scopeName}` : label;

  const [data, setData] = useState<MyEntityPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const loadPage = useCallback(async () => {
    if (!scope || !scopeId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setAccessDenied(false);

    try {
      const response = await sendRequest<MyEntityPageData, unknown>(
        'GET',
        `${scopeSegment}/${scopeId}/${pageSlug(scope)}`
      );

      if (response.status === 403) {
        setAccessDenied(true);
        setData(null);
        return;
      }

      const raw = response?.getData?.() ?? response?.data ?? null;
      if (raw && typeof raw === 'object') {
        setData(raw);
      } else {
        setError('Failed to load this page.');
        setData(null);
      }
    } catch {
      setError('Failed to load this page. Please try again.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId, scopeSegment]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (accessDenied) {
      navigate('/entities', { replace: true, state: { accessDenied: true } });
    }
  }, [accessDenied, navigate]);

  const { permittedActions } = usePermissions({
    scopeType: scope,
    scopeId,
    actionCodes: [
      ACTION_CODES.UPDATE_CHURCH_MEMBERSHIP,
      ACTION_CODES.OVERRIDE_CHURCH_MEMBERSHIP_COUNT,
    ],
  });

  const canUpdateMembership = useMemo(
    () => permittedActions.includes(ACTION_CODES.UPDATE_CHURCH_MEMBERSHIP),
    [permittedActions]
  );
  const canOverrideMembership = useMemo(
    () => permittedActions.includes(ACTION_CODES.OVERRIDE_CHURCH_MEMBERSHIP_COUNT),
    [permittedActions]
  );

  if (accessDenied) {
    return null;
  }

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="dashboard-page">
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>{error ?? 'Unable to load this page.'}</span>
          <Button variant="outline-danger" size="sm" onClick={loadPage}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const isChurch = scope === 'CHURCH';
  const districtLine = isChurch && data.district_name?.trim()
    ? `District: ${data.district_name.trim()}`
    : null;

  return (
    <div className="dashboard-page membership-page">
      <div className="ntcog-page-title-block">
        <h1 className="ntcog-page-heading mb-0">
          {title}
          {isChurch && data.is_head_church ? <HeadChurchLabel /> : null}
        </h1>
        {districtLine && (
          <p className="text-muted mb-0 mt-1">{districtLine}</p>
        )}
      </div>

      {data.officers && <OfficersCard officers={data.officers} />}
      {data.membership_error && (
        <p className="text-muted small mb-4" role="status">Membership couldn't load</p>
      )}
      {data.membership && (
        <MembershipSection
          membership={data.membership}
          apiPath={`${scopeSegment}/${scopeId}`}
          scope={scope}
          canUpdate={canUpdateMembership}
          canOverride={canOverrideMembership}
          onSaved={loadPage}
        />
      )}
    </div>
  );
};

export default MyEntityPage;
